import { useEffect } from 'react'

import { useUpdateLocaleMutation } from '@/features/profile/hooks/useProfile'
import { useLocaleStore, type Locale } from '@/shared/store/localeStore'

import { useCurrentUser } from '../store/authStore'

const VALID_LOCALES: readonly Locale[] = ['es', 'en'] as const

function isValidLocale(value: string | null | undefined): value is Locale {
  return value === 'es' || value === 'en'
}

/**
 * Sincroniza `users.locale` (backend) con `localeStore` (FE) cada vez que
 * cambia el `currentUser`. Reglas:
 *
 * - `userToggled === true` → el localStorage gana. PATCH al backend con
 *   el locale del store. Tras el success, limpia el flag.
 * - `userToggled === false` → el backend gana. Acepta `user.locale` en
 *   el store sin marcar toggled (es solo un sync inicial).
 *
 * Si ya coinciden, no hace nada. En una ruta con idioma fijo (`LocaleScope`) tampoco: ahí manda
 * la URL.
 *
 * El flag `userToggled` NO se persiste — arranca false en cada nueva
 * sesión del browser. Esto evita PATCHes innecesarios en cada refresh.
 */
export function useLocaleSync() {
  const user = useCurrentUser()
  const updateLocale = useUpdateLocaleMutation()

  useEffect(() => {
    if (!user) return
    if (!isValidLocale(user.locale)) return

    const { locale, userToggled, urlScope, acceptBackendLocale, clearToggleFlag } =
      useLocaleStore.getState()

    // En una ruta con idioma fijo (`/en/*`, ayuda…) la URL manda sobre el idioma de la cuenta.
    if (urlScope !== null) return

    if (user.locale === locale) {
      // Ya coincide, solo limpio el flag por si quedó colgado.
      if (userToggled) clearToggleFlag()
      return
    }

    if (userToggled && VALID_LOCALES.includes(locale)) {
      // LocalStorage gana: PATCH al backend con el valor del store.
      updateLocale.mutate(locale, {
        onSuccess: () => clearToggleFlag(),
      })
    } else {
      // Backend gana: acepta el valor remoto en el store.
      acceptBackendLocale(user.locale)
    }
    // updateLocale ref es estable, solo depende del user.locale para reaccionar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.locale])
}
